import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { motion } from 'motion/react'
import { useCallback, useEffect, useState } from 'react'
import type { RealTimeUserNotification } from '~/api/queries/user'
import { getNotificationMetas } from '~/components/Layout/Header/Notification/common'
import {
  notificationFlashItem,
  notificationIcon,
  notificationIconVariants,
  notificationIconWrapper,
  notificationItem,
  notificationItemContent,
  notificationItemDeleteTriggerIcon,
  notificationItemFlashDeleteTrigger,
  notificationItemSubtitle,
  notificationItemTitle
} from '~/components/Layout/Header/Notification/Notifications.css'
import { useConfig } from '~/domains/configuration/queries/config'
import { useOpenTaskAside } from '~/domains/tasks/hooks/useOpenTaskAside'
import { useSafeNavigateToLinkFromLegacy } from '~/hooks/useSafeNavigateToLinkFromLegacy'

export const FlashNotification = ({
  notification,
  onClose
}: {
  notification: RealTimeUserNotification
  onClose?: (slug: string) => void
}) => {
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const navigateToV3 = useSafeNavigateToLinkFromLegacy()
  const openTaskAside = useOpenTaskAside()
  const [timeoutId, setTimeoutId] = useState<number | null>(null)
  const { title, subtitle, icon, onClick } = getNotificationMetas(
    notification,
    legacyUrl,
    navigateToV3,
    { openTask: (taskId) => openTaskAside(taskId) }
  )

  // Dismissed on click too: the target now opens over the page instead of navigating away,
  // so the flash would otherwise stay on top of it until its auto-dismiss
  const onFlashClick = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    onClose?.(notification.slug)
    onClick()
  }

  const autoDismiss = useCallback(() => {
    if (!timeoutId) {
      setTimeoutId(
        window.setTimeout(() => {
          onClose?.(notification.slug)
        }, 10_000)
      )
    }
  }, [onClose, timeoutId, notification.slug])

  useEffect(() => {
    autoDismiss()
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        setTimeoutId(null)
      }
    }
  }, [autoDismiss, timeoutId])

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={clsx(notificationItem, notificationFlashItem)}
      onMouseEnter={() => {
        if (timeoutId) {
          clearTimeout(timeoutId)
          setTimeoutId(null)
        }
      }}
      onMouseLeave={autoDismiss}
    >
      <div
        onClick={() => {
          onClose?.(notification.slug)
          if (timeoutId) {
            clearTimeout(timeoutId)
          }
        }}
        className={notificationItemFlashDeleteTrigger}
      >
        <FontAwesomeIcon
          icon={faXmark}
          className={notificationItemDeleteTriggerIcon}
        />
      </div>
      <div className={notificationIconWrapper} onClick={onFlashClick}>
        {icon && (
          <span
            className={clsx(
              notificationIcon,
              notificationIconVariants[notification.type]
            )}
          >
            <FontAwesomeIcon icon={icon} />
          </span>
        )}
      </div>
      <div onClick={onFlashClick} className={notificationItemContent}>
        <div className={clsx(notificationItemTitle)}>{title}</div>
        <div className={notificationItemSubtitle}>{subtitle}</div>
      </div>
    </motion.div>
  )
}
